// RITA-1599 (Fund 61) — Hinweis oben auf der Berater-Seite, wenn das Speichern die geltende Vorlage für Vertrag,
// Anschreiben Versand oder Anschreiben Rücksendung ändert (Wortlaut mit Julia am 03.11.2026 abgestimmt).
//
// Verglichen wird die Vorlagenwahl der Karte „Vertragserstellung“ vor dem Speichern mit der neu abgefragten danach,
// je Art: anderer Vertrag, kein Vertrag mehr (keine oder mehrere gleich gute Vorlagen) oder wieder einer. Auslöser
// sind nur Änderungen an den Feldern unten (Vertragsinhaber, seine Art, Geschlecht, Vertragsart, Firma); Speicherungen
// ohne solche Folgen bleiben ohne Meldung. Die Reihenfolge
// der Arten folgt dem Ticket (Vertrag, Anschreiben Versand, Anschreiben Rücksendung).
import type { ContractCreationKind } from 'global-intern-types';
import type { Vertragsauswahl, VertragsauswahlDokument } from '../api/vertragserstellung';
import { ART_TITEL, fuerBerater, GESUCHTE_VORLAGE, VERTRAGSINHABER_ART_FEHLT } from './vertragserstellungTexte';

/** Die Felder des Beraters, deren Änderung die Vorlagenwahl ändern kann, mit ihrer Bezeichnung in der Maske. */
export const VORLAGEN_AUSLOESER = [
  { feld: 'business_owner', bezeichnung: 'Abweichender Vertragsinhaber' },
  { feld: 'business_owner_type', bezeichnung: 'Vertragsinhaber ist' },
  { feld: 'is_male', bezeichnung: 'Geschlecht' },
  { feld: 'contract_type_id', bezeichnung: 'Vertragsart' },
  // Entscheidung Julia 03.11.2026: auch ein Wechsel der Firma tauscht die Vorlagen und wird gemeldet.
  { feld: 'branch_office_id', bezeichnung: 'Firma' },
] as const;

/** Gespeicherter bzw. gesendeter Berater; gelesen werden nur die Auslöser-Felder. */
export type Ausloeserwerte = Readonly<Record<string, unknown>>;

/** Leere Angaben gleich behandeln: null, undefined und Leerzeichen sind „nichts eingetragen“. */
const normal = (wert: unknown): string => (wert === null || wert === undefined ? '' : String(wert).trim());

/** Bezeichnungen der Auslöser-Felder, die sich zwischen gespeichertem und neuem Stand unterscheiden. */
export function geaenderteAusloeser(vorher: Ausloeserwerte, nachher: Ausloeserwerte): string[] {
  return VORLAGEN_AUSLOESER.filter(({ feld }) => normal(vorher[feld]) !== normal(nachher[feld])).map(
    ({ bezeichnung }) => bezeichnung,
  );
}

const ARTEN: readonly ContractCreationKind[] = ['contract', 'dispatch_letter', 'return_letter'];

type Wechsel = 'keinMehr' | 'anders' | 'wieder';

/** Nomen je Art in den Titeln („kein Vertrag“, „ein anderes Anschreiben“). */
const NOMEN: Record<ContractCreationKind, { kein: string; anders: string; ein: string }> = {
  contract: { kein: 'kein Vertrag', anders: 'ein anderer Vertrag', ein: 'ein Vertrag' },
  dispatch_letter: { kein: 'kein Anschreiben', anders: 'ein anderes Anschreiben', ein: 'ein Anschreiben' },
  return_letter: {
    kein: 'kein Rücksendungs-Anschreiben',
    anders: 'ein anderes Rücksendungs-Anschreiben',
    ein: 'ein Rücksendungs-Anschreiben',
  },
};

export const MEHRERE_TITEL = 'Für diesen Berater gelten jetzt andere Vorlagen';
export const ZUR_VERTRAGSERSTELLUNG = 'Zur Vertragserstellung';

export interface VorlagenwechselHinweis {
  /** Gelb, sobald für eine Art keine Vorlage mehr passt; sonst blau. */
  schwere: 'warning' | 'info';
  titel: string;
  zeilen: string[];
}

const gefunden = (dokument: VertragsauswahlDokument | undefined) =>
  dokument?.status === 'found' && dokument.template ? dokument.template : null;

function wechselArt(vorher: VertragsauswahlDokument | undefined, nachher: VertragsauswahlDokument | undefined) {
  const alt = gefunden(vorher);
  const neu = gefunden(nachher);
  if (alt && neu) return alt.id === neu.id ? null : 'anders';
  if (alt) return 'keinMehr';
  if (neu) return 'wieder';
  return null;
}

/** „die Änderung bei „Geschlecht““, mehrere: „die Änderungen bei „Geschlecht“ und „Vertragsart““. */
export function grundText(felder: string[]): string {
  const genannt = felder.map((feld) => `„${feld}“`);
  if (genannt.length <= 1) return `Grund: die Änderung bei ${genannt[0] ?? ''}.`;
  return `Grund: die Änderungen bei ${genannt.slice(0, -1).join(', ')} und ${genannt[genannt.length - 1]}.`;
}

/** Warum jetzt keine Vorlage passt: fehlende Art des Vertragsinhabers, zwei gleich gute oder keine. */
function jetztKeine(kind: ContractCreationKind, auswahl: Vertragsauswahl, dokument?: VertragsauswahlDokument): string {
  if (kind === 'contract' && auswahl.business_owner_type_missing) return `Jetzt: ${VERTRAGSINHABER_ART_FEHLT}`;
  if (dokument?.status === 'ambiguous') return `Jetzt passen ${dokument.candidates.length} Vorlagen gleich gut.`;
  const gender = kind === 'contract' ? auswahl.contract_gender : auswahl.gender;
  const kriterien = [
    auswahl.branch_office_name ?? 'unbekannte Firma',
    auswahl.contract_type_name ?? 'ohne Vertragsart',
    fuerBerater(gender),
  ].join(' · ');
  return `Jetzt passt keine ${GESUCHTE_VORLAGE[kind]}. Gesucht wird eine für ${kriterien}.`;
}

/**
 * Der Hinweis nach dem Speichern oder null, wenn sich keine Vorlage geändert hat (oder kein Auslöser geändert wurde).
 * `vorher`/`nachher` sind die Vorlagenwahl der Karte (alle drei Arten) vor bzw. nach dem Speichern.
 */
export function vorlagenwechselHinweis(
  vorher: Vertragsauswahl,
  nachher: Vertragsauswahl,
  ausloeser: string[],
): VorlagenwechselHinweis | null {
  if (ausloeser.length === 0 || vorher.agent_id !== nachher.agent_id) return null;
  const finde = (auswahl: Vertragsauswahl, kind: ContractCreationKind) =>
    auswahl.documents.find((dokument) => dokument.kind === kind);
  const wechsel = ARTEN.flatMap((kind) => {
    const alt = finde(vorher, kind);
    const neu = finde(nachher, kind);
    const art = wechselArt(alt, neu);
    return art ? [{ kind, art: art as Wechsel, alt, neu }] : [];
  });
  if (wechsel.length === 0) return null;
  const name = (dokument?: VertragsauswahlDokument) => `„${gefunden(dokument)?.name ?? ''}“`;
  const schwere = wechsel.some((eintrag) => eintrag.art === 'keinMehr') ? 'warning' : 'info';
  const grund = grundText(ausloeser);

  if (wechsel.length === 1) {
    const [{ kind, art, alt, neu }] = wechsel;
    if (art === 'keinMehr') {
      return {
        schwere,
        titel: `Für diesen Berater passt jetzt ${NOMEN[kind].kein} mehr`,
        zeilen: [`Bisher: ${name(alt)}. ${jetztKeine(kind, nachher, neu)} ${grund}`],
      };
    }
    if (art === 'anders') {
      return {
        schwere,
        titel: `Für diesen Berater gilt jetzt ${NOMEN[kind].anders}`,
        zeilen: [`Bisher: ${name(alt)}. Jetzt: ${name(neu)}. ${grund}`],
      };
    }
    return {
      schwere,
      titel: `Für diesen Berater passt jetzt wieder ${NOMEN[kind].ein}: ${name(neu)}`,
      zeilen: [grund],
    };
  }

  const zeilen = wechsel.map(({ kind, art, alt, neu }) => {
    if (art === 'keinMehr') return `${ART_TITEL[kind]}: bisher ${name(alt)}. ${jetztKeine(kind, nachher, neu)}`;
    if (art === 'anders') return `${ART_TITEL[kind]}: bisher ${name(alt)}, jetzt ${name(neu)}.`;
    return `${ART_TITEL[kind]}: jetzt wieder ${name(neu)}.`;
  });
  return { schwere, titel: MEHRERE_TITEL, zeilen: [...zeilen, grund] };
}
